import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { consumeAuthReturnTo, SignInWithEmail } from "src/handlers/Account";
import { ensureActiveProjectSession } from "src/handlers/Projects";
import { syncdown } from "src/handlers/Sync";

function EmailLoginDialog() {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Phase I2: re-auth mode preserves the current route + in-memory editor
  // state. When the dialog opens in response to `netbase:authExpired`, on
  // success we close the dialog and dispatch `netbase:forceSync` instead of
  // navigating anywhere.
  const [reAuth, setReAuth] = useState(false);

  useEffect(() => {
    const handleShow = (event) => {
      setOpen(true);
      setError("");
      setReAuth(Boolean(event?.detail?.reAuth));
    };

    window.addEventListener("netbase:showEmailLogin", handleShow);
    return () => window.removeEventListener("netbase:showEmailLogin", handleShow);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setEmail("");
    setPassword("");
    setError("");
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!email.trim() || !password) return;
      // keep reAuth in the dependency list so successful submit resets it correctly.

      setLoading(true);
      setError("");

      try {
        const session = await SignInWithEmail(email.trim(), password);

        if (session) {
          const project = await ensureActiveProjectSession();
          if (project) {
            await Promise.all([syncdown("router"), syncdown("vlan"), syncdown("switch")]);
          }
        }

        handleClose();

        // Phase I2: re-auth flow preserves the editor page. Only flush any
        // pending writes; never navigate, so unsaved in-memory edits stay.
        if (reAuth) {
          setReAuth(false);
          try {
            window.dispatchEvent(new CustomEvent("netbase:forceSync"));
          } catch {
            // ignore
          }
          return;
        }

        // Auth state has already been broadcast by SignInWithEmail, so
        // protected routes and the sidebar will re-render without a reload.
        const returnTo = consumeAuthReturnTo("/components/router");
        navigate(returnTo, { replace: true });
      } catch (err) {
        setError(err?.message || "Invalid email or password.");
      } finally {
        setLoading(false);
      }
    },
    [email, password, handleClose, navigate, reAuth]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <DialogContent sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {reAuth ? "Your session expired" : "Sign in to NetBase"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {reAuth
                  ? "Sign back in to keep editing. Your unsaved changes are still here."
                  : "Enter your account credentials."}
              </Typography>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField
              autoFocus
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || !email.trim() || !password}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EmailLoginDialog;
